(ns minorodata.components.admin.drawers
  (:require ["lucide-react" :refer [X]]
            [applied-science.js-interop :as js-interop]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.components.admin.forms :as admin-forms]
            [minorodata.components.warehouse-credentials.core :as wcredentials]
            [minorodata.utils :as utils]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [ui.drawer]
            [ui.button]
            [ui.alert :refer [alert alert-title]]
            [ui.dialog :refer [dialog-close-x dialog-content dialog-root
                               dialog-title dialog-title-bg]]
            [ui.typography :as typography]))

(defn check-for-duplicate-users [fields]
  (let [current-user @(re-posh/subscribe [:user/pull])
        admin-data   @(re-posh/subscribe [:client-data/pull [:client-data/cid (:user/cid current-user)]])
        users        (:client-data/users admin-data)
        emails       (map :user-account/login-name users)]
    (when (some? (utils/value-included (:login-name fields) emails))
      {:login-name ["Login name already taken"]})))


(defn new-user [{:keys [open on-close]}]
  (let [client-roles @(re-posh/subscribe [:users.subs/roles])]
    [ui.drawer/drawer
     {:open open
      :on-open-change on-close
      :content [:div {:class "max-w-md"}
                [typography/h2 "Create new user"]

                [form-component/form
                 (->form-model
                  {:id        :new-client-user-form
                   :on-submit (fn [data]
                                (admin-forms/submit-new-user-form data on-close))
                   :validate  (fn [fields]
                                (let [form-errors (form-component/validate-form ::admin-forms/new-user-form fields)]
                                  (if (some? form-errors)
                                    form-errors
                                    (check-for-duplicate-users fields))))
                   :clean-up  #(dispatch [:form-model/delete [:form-model/id :new-client-user-form]])
                   :fields    [{:name          :user-name
                                :value         ""}
                               {:name          :login-name
                                :value         ""}
                               {:name          :role
                                :process       (fn [v] v)
                                :value         (some-> client-roles first :role/id)}
                               {:name          :password
                                :value         ""
                                :default-value ""}]})
                 (fn [fields errors on-submit]
                   [admin-forms/new-user-account-form-render
                    {:on-submit  on-submit
                     :errors     errors
                     :fields     fields
                     :user-name  (:user-name fields)
                     :login-name (:login-name fields)
                     :role       (:role fields)
                     :password   (:password fields)
                     :roles      client-roles}])]]}]))


(defn edit-client-user [{:keys [admin-data open user-id on-close on-delete]}]
  [ui.drawer/drawer
   {:open     open
    :on-open-change on-close
    :content (let [{:client-data/keys [users]} admin-data

                   user  (utils/find-by-field users :user-account/id user-id)
                   {:user-account/keys [id user-name login-name role-id is-sso]} user

                   client-roles @(re-posh/subscribe [:users.subs/roles])
                   client-role  (or (utils/find-by-field client-roles :role/id role-id)
                                    (first client-roles))
                   role-id      (:role/id client-role)
                   role-name    (:role/name client-role)]
               [:div {:class "max-w-lg"}
                [dialog-title "Edit user"]

                [:div {:class "my-4"}
                 [typography/h3 "Kleene credentials"]

                 [form-component/form
                  (->form-model
                   {:id        :edit-client-user-form
                    :on-submit (fn [data]
                                 (on-close)
                                 (admin-forms/submit-user-form data))
                    :validate  (partial form-component/validate-form ::admin-forms/user-form)
                    :clean-up  #(dispatch [:form-model/delete [:form-model/id :edit-client-user-form]])
                    :fields    [{:name     :user-name
                                 :disabled is-sso
                                 :value    user-name}
                                {:name     :login-name
                                 :disabled is-sso
                                 :value    login-name}
                                {:name    :role
                                 :process (fn [v] v)
                                 :value   role-id}
                                {:name  :role-name
                                 :value role-name}
                                {:name  :id
                                 :value id}]})
                  (fn [fields errors on-submit]
                    [admin-forms/edit-user-account-form-render
                     {:on-submit  on-submit
                      :errors     errors
                      :fields     fields
                      :user-name  (:user-name fields)
                      :login-name (:login-name fields)
                      :id         (:id fields)
                      :role       (:role fields)
                      :roles      client-roles}])]]

                [:div {:class "my-4"}
                 [typography/h3 {} "Warehouse credentials"]

                 [wcredentials/warehouse-credentials-form {:user-id            user-id
                                                           :allow-enforcement? true}]]

                [:div {:class "mt-4 pt-4 border-t"}
                 [:div {:class "flex flex-col gap-2 items-start"}
                  [typography/h3 "Delete user"]

                  [ui.button/button {:variant :error
                                     :on-click  #(on-delete user-id)}
                   "Delete"]]]])}])

(defn update-form-field
  [form-key field-key value]
  (let [form-state      @(re-posh/subscribe [:form-model/pull [:form-model/id form-key]])
        {:keys [db/id]} (utils/find-first #(= (name (:form-field/name %)) field-key) (:form-model/fields form-state))]
    (dispatch-sync [:form-field/update id {:form-field/value value} {:replace true}])))

(defn permission->field [k key form-key role-permissions-set default-value permissions]
  (let [combined?            (admin-forms/combined-permissions key)
        qualified-permission (keyword (str (name key) "/" (name k)))
        update-field         (partial update-form-field form-key)
        get-full-name        (fn [& strs] (apply str (name key) strs))
        get-keyword          (fn [& strs] (keyword (apply get-full-name strs)))]
    {:name    (get-keyword "." (name k))
     :value   (or default-value
                  (if combined?
                    (and (boolean (role-permissions-set (get-keyword "/read")))
                         (boolean (role-permissions-set (get-keyword "/write"))))
                    (boolean (role-permissions-set qualified-permission))))
     :process (fn [e]
                (let [new-value e]
                  (case key
                    :transforms  (update-field "sql-browser.read+write" new-value)
                    :sql-browser (update-field "transforms.read+write" new-value)
                    nil)
                  (when (= #{:read :write} permissions)
                    (cond
                      (and new-value (= k :write))      (update-field (get-full-name ".read") true)
                      (and (not new-value) (= k :read)) (update-field (get-full-name ".write") false)))
                  new-value))}))

(defn permission-fields
  [form-key role-permissions-set & [default-value]]
  (let [fields (->> admin-forms/permission-sections
                    (mapv :items)
                    flatten
                    (reduce (fn [acc {:keys [key permissions]}]
                              (concat acc (mapv
                                           #(permission->field % key form-key role-permissions-set default-value permissions)
                                           permissions)))
                            []))]
    fields))

(defn new-role [{:keys [open on-close]}]
  [dialog-root {:open           open
                :on-open-change #(when-not % (on-close))}
   [dialog-content {:x-close false}
    [dialog-title-bg {:class "sticky -top-4"}
     [dialog-title "New role"]
     [dialog-close-x]]

    [:div {:class "my-2"}
     [form-component/form
      (->form-model
       {:id        :new-role-form
        :on-submit (fn [data]
                     (on-close)
                     (admin-forms/submit-new-role-form data))
        :clean-up  #(dispatch [:form-model/delete [:form-model/id :new-role-form]])
        :fields    (concat [{:name :name :value ""}]
                           (permission-fields :new-role-form #{} true))})
      (fn [fields errors on-submit]
        [admin-forms/edit-role-form-render
         {:on-submit  on-submit
          :creds?     false
          :errors     errors
          :fields     fields
          :name       (:name fields)
          :transforms (:transforms fields)}])]]]])

(defn edit-role [{:keys [open role-id roles-data on-close]}]
  [dialog-root {:open           open
                :on-open-change #(when-not % (on-close))}
   (let [role (utils/find-by-field (:client-roles/roles roles-data) :role/id role-id)
         {:role/keys [id permissions]
          role-name :role/name} role]

     [dialog-content {:x-close false}
      [dialog-title-bg {:class "sticky -top-4"}
       [dialog-title "Edit role"]
       [dialog-close-x]]

      [:div {:class "my-2 overflow-y-auto"}
       [form-component/form
        (->form-model
         {:id        :edit-role-form
          :on-submit (fn [data]
                       (on-close)
                       (admin-forms/submit-edit-role-form id data))
          :clean-up  #(dispatch [:form-model/delete [:form-model/id :edit-role-form]])
          :fields    (-> [{:name :name :value role-name}]
                         (concat (permission-fields :edit-role-form (set permissions))))})
        (fn [fields errors on-submit]
          [admin-forms/edit-role-form-render
           {:on-submit  on-submit
            :errors     errors
            :fields     fields
            :name       (:name fields)
            :show-warning true}])]]

      [:div {:class "my-4"}
       [typography/h3 {:class "mb-1"} "Warehouse credentials"]
       [alert {:class   "my-1"
               :variant :info}
        [alert-title "Optional"]
        [typography/p {:class "text-xs"}
         "All users with this Role can be made to use the warehouse credentials added here"]]
       [alert {:class "my-1"}
        [alert-title "Warning"]
        [typography/p {:class "text-xs"}
         "If an Admin or a user adds their own personal credentials to a user profile, then those will OVERRIDE the Role credentials here."]]
       [:div {:class "mt-4"}
        [wcredentials/warehouse-credentials-form {:role-id role-id}]]]])])


(defn change-client-user-password [{:keys [open user-id admin-data on-close]}]
  [ui.drawer/drawer
   {:open open
    :on-open-change on-close
    :content (let [users (:client-data/users admin-data)
                   user  (utils/find-by-field users :user-account/id user-id)
                   {:user-account/keys [id user-name login-name role is-blocked]} user
                   title (if is-blocked
                           "Unblocking "
                           "Changing password for ")]
               [:div {:class "max-w-md"}
                [typography/h2 title [:u user-name]]

                [form-component/form
                 (->form-model
                  {:id        :password-client-user-form
                   :on-submit (fn [data]
                                (admin-forms/submit-user-password-form data on-close))
                   :validate  (partial form-component/validate-form ::admin-forms/password-form)
                   :fields    [{:name          :password
                                :value         ""
                                :default-value ""}
                               {:name          :role
                                :process       (fn [v] v)
                                :value         role}
                               {:name          :user-name
                                :value         user-name}
                               {:name          :login-name
                                :value         login-name}
                               {:name          :id
                                :default-value id
                                :value         id}]})
                 (fn [fields errors on-submit]
                   [admin-forms/change-password-user-account-form-render
                    {:on-submit on-submit
                     :errors    errors
                     :fields    fields
                     :password  (:password fields)
                     :id        (:id fields)}])]])}])


(defn delete-client-user [{:keys [open user-id admin-data on-close]}]
  [ui.drawer/drawer
   {:open     open
    :on-open-change on-close
    :content (let [users (:client-data/users admin-data)
                   user  (utils/find-by-field users :user-account/id user-id)
                   {:user-account/keys [username]} user]
               [:div {:class "max-w-md"}
                [typography/h2 "Do you really wish to delete " [:u username]]

                [:div {:class "mt-4"}
                 [ui.button/button {:variant    :error
                                    :on-click   (fn []
                                                  (on-close)
                                                  (admin-forms/submit-delete-user user-id))}
                  "Delete user"]]])}])
